import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  getLastShownIndex,
  hasBlockUserField,
  isBlockUserFieldFilled,
} from "logic/logics";
import { usePrevious } from "hooks/usePrevious";
import * as _ from "lodash";
import BlockV2 from "components/task/content/v2/block/BlockV2";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import { ProgramContentType } from "data/BlockComponent";
import { isMobile } from "react-device-detect";
import { CircularProgress, Sheet, Stack } from "@mui/joy";
import { invalidTextareaIndexAtom } from "recoil/invalidTextareaIndexAtom";
import useDetectIOS from "hooks/useDetectIOS";

export type TaskContentV2ViewerMode = "auto" | "patient" | "coach";

export default function TaskContentV2({
  taskKey,
  data,
  setData,
  moveToIndex,
  setMoveToIndex,
  viewerMode = "auto",
}: {
  taskKey: string;
  data?: ProgramContentType[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  moveToIndex?: number;
  setMoveToIndex: React.Dispatch<React.SetStateAction<number | undefined>>;
  viewerMode?: TaskContentV2ViewerMode;
}) {
  const lastIndex = getLastShownIndex(data);
  const [doneWithEnter, setDoneWithEnter] = useState<boolean>(false);

  const user = useRecoilValue(userAtom);
  const isCoach =
    viewerMode === "auto"
      ? !!user?.accessToken && user?.role !== "patient"
      : viewerMode === "coach";

  const { isIOS } = useDetectIOS();

  const focusElement = (el: HTMLElement) => {
    el.focus();
    try {
      const input = el as HTMLInputElement | HTMLTextAreaElement;
      const len = input.value?.length || 0;
      input.setSelectionRange?.(len, len);
    } catch {}

    if (isIOS) {
      try {
        el.click();
      } catch {}
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>();

  const smoothScrollTo = useCallback((index: number) => {
    const blockEl = blockRefs.current[index];
    if (!blockEl) return;

    blockEl.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const previousData = usePrevious(data);

  const blockRefs = useRef<HTMLDivElement[]>([]);

  const resetInvalidTextarea = useResetRecoilState(invalidTextareaIndexAtom);

  // Skip the first auto-scroll due to initial data hydration.
  const skipNextAutoScrollRef = useRef<boolean>(true);

  useEffect(() => {
    resetInvalidTextarea();
    return () => {
      resetInvalidTextarea();
    };
  }, [taskKey, resetInvalidTextarea]);

  useEffect(() => {
    skipNextAutoScrollRef.current = true;
  }, [taskKey]);

  const focusBlockInput = (index: number) => {
    if (isCoach) return;
    const blockEl = blockRefs.current[index];
    if (!blockEl) return;
    const focusables = Array.from(
      blockEl.querySelectorAll<HTMLElement>(
        'textarea, input[type="text"], input[type="number"], input[type="tel"], .ql-editor'
      )
    );
    const inputEl = focusables.find(
      (el) =>
        !el.closest("[data-feedback-cell]") &&
        !el.closest("[data-disable-autofocus]")
    );
    if (inputEl) {
      focusElement(inputEl);
    }
  };

  useEffect(() => {
    if (data && data.length > 0 && previousData) {
      const changedIndex = data.findIndex(
        (element, index) => !_.isEqual(element, previousData[index])
      );

      if (
        changedIndex > -1 &&
        hasBlockUserField(data[changedIndex]) &&
        isBlockUserFieldFilled(data[changedIndex]) &&
        !data[changedIndex].hideIfPatient &&
        ((data[changedIndex].nextBlockId &&
          data[changedIndex].nextBlockId !==
            (previousData[changedIndex] as ProgramContentType).nextBlockId) ||
          data[changedIndex].autoOpenNext)
      ) {
        complete(changedIndex);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!isCoach && data && data.length > 0) {
      if (
        lastIndex < data.length - 1 &&
        data[lastIndex].isShown &&
        !data[lastIndex].indentation &&
        (data[lastIndex].noArrow ||
          data[lastIndex].hideIfPatient ||
          data[lastIndex].isAlwaysHidden) &&
        !data[lastIndex].isEnd
      ) {
        complete(lastIndex);
      }
    }
  }, [data, isCoach, lastIndex]);

  useEffect(() => {
    function getShownNumber(nextData: ProgramContentType[]) {
      return nextData.filter((element) => element.isShown && !element.isHidden)
        .length;
    }

    if (
      !isCoach &&
      previousData &&
      data &&
      lastIndex > -1 &&
      ((lastIndex === data.length - 1) ||
        (lastIndex < data.length - 1 &&
          (!data[lastIndex + 1].isShown || data[lastIndex + 1].isHidden))) &&
      !_.isEqual(previousData, data)
    ) {
      const prevShown = getShownNumber(previousData);
      const nowShown = getShownNumber(data);

      if (prevShown > 0 && nowShown === prevShown + 1) {
        if (skipNextAutoScrollRef.current) {
          skipNextAutoScrollRef.current = false;
          return;
        }
        setMoveToIndex(lastIndex);
      }
    }
  }, [taskKey, data, previousData, isCoach, lastIndex, setMoveToIndex]);

  const openNextBlock = useCallback(
    (index: number) => {
      if (!data) return index;
      const isLastBlock = index === data.length - 1;
      if (isLastBlock || data[index].isEnd) return index;

      const dataTemp = _.cloneDeep(data);
      const nextBlockIndex = dataTemp.findIndex(
        (element, blockIndex) =>
          blockIndex > index &&
          element.blockId &&
          element.blockId === dataTemp[index].nextBlockId
      );
      const targetIndex = nextBlockIndex > -1 ? nextBlockIndex : index + 1;
      dataTemp[targetIndex].isShown = true;
      dataTemp[targetIndex].isHidden = false;

      flushSync(() => {
        setData(dataTemp);
      });

      return targetIndex;
    },
    [data, setData]
  );

  const complete = useCallback(
    (index: number) => {
      (document.activeElement as HTMLElement)?.blur();
      if (
        data &&
        (data[index].isAlwaysHidden || isBlockUserFieldFilled(data[index], data, user))
      ) {
        const nextIdx = openNextBlock(index);
        focusBlockInput(nextIdx);
      }
    },
    [data, openNextBlock, user]
  );

  useEffect(() => {
    const goNext = (e: KeyboardEvent) => {
      if (
        !isMobile &&
        (e.code === "Enter" ||
          e.code === "NumpadEnter" ||
          e.key === "Enter" ||
          e.key === "NumpadEnter")
      ) {
        if (data && data[lastIndex] && !e.shiftKey) {
          e.preventDefault();
          complete(lastIndex);
        }
      }
    };

    if (!isCoach) {
      window.addEventListener("keypress", goNext);
      return () => window.removeEventListener("keypress", goNext);
    }
  }, [data, taskKey, complete, lastIndex, isCoach]);

  useEffect(() => {
    if (moveToIndex !== undefined && blockRefs.current[moveToIndex]) {
      smoothScrollTo(moveToIndex);
    }
  }, [moveToIndex, smoothScrollTo]);

  useEffect(() => {
    if (data && !containerHeight && scrollRef.current?.offsetHeight) {
      setContainerHeight(scrollRef.current.offsetHeight);
    }
  }, [data, containerHeight]);

  if (!data || data.length === 0) {
    return (
      <Sheet sx={{ backgroundColor: "transparent", height: "100%" }}>
        <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
          <CircularProgress color="neutral" />
        </Stack>
      </Sheet>
    );
  }

  return (
    <div
      id="scrollContainer"
      ref={scrollRef}
      onScroll={() => {
        if (moveToIndex !== undefined) {
          setMoveToIndex(undefined);
        }
      }}
      className="h-full w-full overflow-y-auto pt-[8px] pb-[20px] px-[20px]"
      style={{
        paddingBottom: (containerHeight || 72) / 2,
        overflowX: "hidden",
      }}
    >
      {data.map((blockData, index) => (
        <BlockV2
          ref={(el) => (blockRefs.current[index] = el as HTMLDivElement)}
          key={`${taskKey}_${index}`}
          taskKey={taskKey}
          data={data}
          setData={setData}
          blockData={blockData}
          index={index}
          complete={complete}
          isCurrentIndex={lastIndex === index}
          isLastIndex={index === data.length - 1}
          viewerMode={viewerMode}
          doneWithEnter={doneWithEnter}
          setDoneWithEnter={setDoneWithEnter}
          setMoveToIndex={setMoveToIndex}
        />
      ))}
    </div>
  );
}
